import { motion } from "framer-motion";
import { Building2, ArrowRight, LogIn, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { useState } from "react";

const links = [
  { label: "How It Works", href: "#how-it-works", testid: "nav-link-how-it-works" },
  { label: "Service Rules", href: "#service-rules", testid: "nav-link-service-rules" },
  { label: "Pricing", href: "#pricing", testid: "nav-link-pricing" },
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
];

export const scrollTo = (hash) => {
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(hash, { offset: -80, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[94%] max-w-5xl"
      data-testid="site-nav"
    >
      <div className="glass rounded-full px-5 py-3 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); scrollTo("#top"); }}
          className="flex items-center gap-2.5 group">
          <span className="w-8 h-8 rounded-full bg-terracotta flex items-center justify-center transition-transform duration-300 group-hover:rotate-12">
            <Building2 className="w-4 h-4 text-charcoal" strokeWidth={2.4} />
          </span>
          <span className="font-display font-bold text-sm tracking-wide text-parchment">
            Doorstep Disposal <span className="text-terracotta">Co.</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.testid}
              onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
              className="text-xs font-medium tracking-wide text-mist hover:text-parchment transition-colors duration-300">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href="/admin" data-testid="nav-admin-link"
            className="hidden sm:block text-xs font-mono uppercase tracking-widest text-dim hover:text-parchment transition-colors duration-300">
            Admin
          </a>
          {user ? (
            <div className="flex items-center gap-2" data-testid="nav-user-chip">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-white/15" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-fern flex items-center justify-center text-[10px] font-bold text-parchment">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </span>
              )}
              <span className="hidden lg:block text-xs text-mist max-w-[8rem] truncate">{user.name || user.email}</span>
              <button onClick={logout} data-testid="nav-sign-out-button" title="Sign out"
                className="p-1.5 text-dim hover:text-terracotta transition-colors duration-300">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button onClick={() => setAuthOpen(true)} data-testid="nav-sign-in-button"
              className="flex items-center gap-1.5 text-xs font-medium text-mist hover:text-parchment border border-white/10 hover:border-terracotta/50 px-3.5 py-2 rounded-full transition-colors duration-300">
              <LogIn className="w-3.5 h-3.5" /> Sign In
            </button>
          )}
          <button data-testid="nav-cta-button" onClick={() => scrollTo("#pricing")}
            className="group flex items-center gap-2 bg-parchment text-charcoal text-xs font-semibold px-4 py-2 rounded-full hover:bg-terracotta hover:text-parchment transition-colors duration-300">
            See Plans
            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </motion.header>
  );
}
